'use client';

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { ArrowUpRight, Search } from 'lucide-react';
import { SiteHeader, SiteSearch } from './site-chrome';
import { fetchWikiSearch, type WikiSearchItem } from '../lib/wiki-search';
import './game-search-page.css';

export type GameSearchItem = WikiSearchItem;

const KIND_LABEL: Record<GameSearchItem['kind'], string> = {
  hero: '角色图鉴',
  guide: '攻略',
  community: '社区',
};

const EMPTY_HINTS = ['角色名', '开荒', '配队', '活动'];

function syncSearchUrl(gameDomain: string, value: string) {
  if (typeof window === 'undefined') return;
  const next = value.trim()
    ? `/${gameDomain}/search?q=${encodeURIComponent(value.trim())}`
    : `/${gameDomain}/search`;
  const current = `${window.location.pathname}${window.location.search}`;
  if (current === next) return;
  window.history.replaceState(window.history.state, '', next);
}

/** Hard navigate — vinext Link soft-nav/RSC prefetch crashes under bots-only SSR. */
function hardNavigate(href: string, event?: { preventDefault(): void }) {
  event?.preventDefault();
  if (typeof window !== 'undefined') window.location.assign(href);
}

export default function GameSearchPage({
  gameKey,
  gameDomain,
  gameName,
  initialQuery,
  initialItems = [],
}: {
  gameKey: string;
  gameDomain: string;
  gameName: string;
  initialQuery: string;
  initialItems?: GameSearchItem[];
}) {
  const [query, setQuery] = useState(initialQuery);
  const [submitted, setSubmitted] = useState(initialQuery.trim());
  const [results, setResults] = useState<GameSearchItem[]>(initialItems);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const bootstrapped = useRef(false);

  const runSearch = async (value: string) => {
    const next = value.trim();
    setQuery(next);
    setSubmitted(next);
    syncSearchUrl(gameDomain, next);
    abortRef.current?.abort();
    if (!next) {
      requestId.current += 1;
      setResults([]);
      setLoading(false);
      setError('');
      return;
    }
    const id = ++requestId.current;
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError('');
    try {
      const items = await fetchWikiSearch(gameKey, next, controller.signal);
      if (id !== requestId.current) return;
      setResults(items);
    } catch (err) {
      if (
        controller.signal.aborted ||
        id !== requestId.current ||
        (err instanceof DOMException && err.name === 'AbortError')
      ) {
        return;
      }
      setResults([]);
      setError(err instanceof Error ? err.message : '搜索失败');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    const next = initialQuery.trim();
    if (!next) return;
    if (initialItems.length > 0) {
      syncSearchUrl(gameDomain, next);
      return;
    }
    setLoading(true);
    void runSearch(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only bootstrap from URL
  }, []);

  return (
    <div className="shared-game-shell game-search-shell">
      <SiteHeader banner={false} gameDomain={gameDomain} />
      <div className="gk-header-space" aria-hidden="true" />
      <main className="game-search-page">
        <div className="game-search-panel">
          <header className="game-search-heading">
            <nav className="game-search-breadcrumb" aria-label="面包屑导航">
              <a
                href={`/${gameDomain}`}
                onClick={(event) => hardNavigate(`/${gameDomain}`, event)}
              >
                首页
              </a>
              <span aria-hidden="true">/</span>
              <span>搜索</span>
            </nav>
            <div className="game-search-heading-row">
              <div className="game-search-heading-copy">
                <h1>搜索 {gameName}</h1>
                <span>图鉴 · 攻略 · 社区</span>
              </div>
              <SiteSearch
                query={query}
                onQuery={setQuery}
                onSearch={() => {
                  void runSearch(query);
                }}
                placeholder="输入关键词，回车或点图标搜索"
              />
            </div>
          </header>

          <section className="game-search-results" aria-live="polite">
          <div className="game-search-results-heading">
            <div>
              <Search aria-hidden="true" size={16} />
              <strong>
                {submitted
                  ? loading
                    ? `正在搜索“${submitted}”`
                    : `“${submitted}”的结果`
                  : '输入关键词后点击搜索'}
              </strong>
            </div>
            <span className="game-search-results-count">
              {submitted ? (
                loading ? (
                  '检索中'
                ) : (
                  <>
                    <b>{results.length}</b> 条
                  </>
                )
              ) : (
                '等待搜索'
              )}
            </span>
          </div>

          {error ? (
            <div className="game-search-empty">
              <div className="game-search-empty-icon" aria-hidden="true">
                <Search size={28} />
              </div>
              <strong>搜索暂时不可用</strong>
              <p>{error}</p>
            </div>
          ) : results.length > 0 ? (
            <div className="game-search-result-list">
              {results.map((item, index) => (
                <a
                  href={item.href}
                  key={item.id}
                  className={`game-search-result game-search-result--${item.kind}`}
                  style={
                    {
                      '--reveal-delay': `${Math.min(index, 12) * 28}ms`,
                    } as CSSProperties
                  }
                  onClick={(event) => hardNavigate(item.href, event)}
                >
                  <div className="game-search-result-body">
                    <div className="game-search-result-title">
                      <h2>{item.title}</h2>
                      <small>{KIND_LABEL[item.kind]}</small>
                    </div>
                    {item.description ? <p>{item.description}</p> : null}
                    {item.meta ? <span>{item.meta}</span> : null}
                  </div>
                  <ArrowUpRight
                    aria-hidden="true"
                    size={18}
                    className="game-search-result-arrow"
                  />
                </a>
              ))}
            </div>
          ) : submitted && !loading ? (
            <div className="game-search-empty">
              <div className="game-search-empty-icon" aria-hidden="true">
                <Search size={28} />
              </div>
              <strong>没有匹配「{submitted}」的内容</strong>
              <p>试试更短的关键词，或从下面挑一个方向重新搜。</p>
              <div className="game-search-empty-hints">
                {EMPTY_HINTS.map((hint) => (
                  <button
                    type="button"
                    key={hint}
                    onClick={() => {
                      void runSearch(hint);
                    }}
                  >
                    {hint}
                  </button>
                ))}
              </div>
            </div>
          ) : !submitted ? (
            <div className="game-search-idle">
              <p>输入关键词后，点击搜索图标或按回车查看结果。</p>
            </div>
          ) : null}
          </section>
        </div>
      </main>
    </div>
  );
}
